import type { Dispatch, SetStateAction } from "react"
import { Input } from "~/react/ui/input"
import { Label } from "~/react/ui/label"
import { Checkbox } from "~/react/ui/checkbox"
import { Hint } from "~/react/ui/hint"
import SignatureEditor from "~/react/components/signature_editor"
import type { EmailAccount } from "../email_accounts_filters"

type FormData = {
  daily_limit: number
  default_account: boolean
  sender_name: string
  signature: string
  managedMailbox: {
    first_name: string
    last_name: string
  }
}

type Props = {
  account: EmailAccount
  formData: FormData
  setFormData: Dispatch<SetStateAction<FormData>>
}

export default function EmailAccountProfileTab({
  account,
  formData,
  setFormData,
}: Props) {
  const isBulkEdit = Boolean(account._bulkEdit)
  const canEditSenderName = isBulkEdit
    ? (account._bulkEdit?.canEditSenderName ?? false)
    : account.provider !== "outlook" &&
      account.provider !== "smtp_imap" &&
      !account.managed
  const canEditSignature = isBulkEdit
    ? (account._bulkEdit?.canEditSignature ?? false)
    : account.provider === "smtp_imap" || account.managed
  const canEditManagedFields = isBulkEdit
    ? (account._bulkEdit?.canEditManagedFields ?? false)
    : account.managed
  return (
    <>
      {canEditManagedFields && (
        <div className="row g-4 mb-4">
          <div className="col-6">
            <Label>
              {i18n.t(
                "js.email_accounts_center.email_account.profile.first_name",
              )}
            </Label>
            <Input
              type="text"
              fullWidth
              value={formData.managedMailbox?.first_name || ""}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  managedMailbox: {
                    ...prev.managedMailbox,
                    first_name: e.target.value,
                  },
                }))
              }
            />
          </div>
          <div className="col-6">
            <Label>
              {i18n.t(
                "js.email_accounts_center.email_account.profile.last_name",
              )}
            </Label>
            <Input
              type="text"
              fullWidth
              value={formData.managedMailbox?.last_name || ""}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  managedMailbox: {
                    ...prev.managedMailbox,
                    last_name: e.target.value,
                  },
                }))
              }
            />
          </div>
        </div>
      )}

      {canEditSenderName && (
        <>
          <Label htmlFor={`email-account-sender-name-${account.id}`}>
            {i18n.t(
              "js.email_accounts_center.email_account.profile.sender_name",
            )}
          </Label>
          <Input
            id={`email-account-sender-name-${account.id}`}
            type="text"
            fullWidth
            className="mb-4"
            value={formData.sender_name}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, sender_name: e.target.value }))
            }
            disabled={!isBulkEdit && account.provider === "outlook"}
          />
        </>
      )}

      {canEditSignature && (
        <>
          <Label htmlFor={`email-account-signature-${account.id}`} fullWidth className="mb-2">
            {i18n.t("js.email_accounts_center.email_account.profile.signature")}
          </Label>
          <SignatureEditor
            id={`email-account-signature-${account.id}`}
            value={formData.signature}
            onChange={(value) =>
              setFormData((prev) => ({ ...prev, signature: value }))
            }
            previewSample={{
              firstName:
                formData.managedMailbox?.first_name ||
                account.owner_name?.split(" ")[0] ||
                "",
              lastName:
                formData.managedMailbox?.last_name ||
                account.owner_name?.split(" ").slice(1).join(" ") ||
                "",
            }}
          />
        </>
      )}

      <div className="mb-6 mt-6">
        <Label htmlFor={`email-account-daily-limit-${account.id}`} fullWidth>
          {i18n.t("js.email_accounts_center.email_account.profile.daily_limit")}
        </Label>
        <Input
          id={`email-account-daily-limit-${account.id}`}
          type="number"
          className="mb-2"
          value={formData.daily_limit || ""}
          onChange={(e) =>
            setFormData((prev) => ({
              ...prev,
              daily_limit:
                e.target.value === "" ? 0 : parseInt(e.target.value, 10) || 0,
            }))
          }
        />
        <Hint
          variant="default"
          className="mt-1"
          dangerouslySetInnerHTML={{
            __html: i18n.t(
              "js.email_accounts_center.email_account.profile.daily_limit_hint_html",
            ),
          }}
        />
      </div>

      {!isBulkEdit && (
        <div className="h-form-group mb-4">
          <div className="h-form-group__input">
            <Checkbox
              id={`email_account_default_account_${account.id}`}
              checked={formData.default_account}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  default_account: e.target.checked,
                }))
              }
            />
          </div>
          <div className="h-form-group__infos">
            <Label htmlFor={`email_account_default_account_${account.id}`}>
              {i18n.t(
                "js.email_accounts_center.email_account.profile.default_account_label",
              )}
            </Label>
            <Hint>
              {i18n.t(
                "js.email_accounts_center.email_account.profile.default_account_hint",
              )}
            </Hint>
          </div>
        </div>
      )}
    </>
  )
}
